import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  alpha,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSubscription } from "../../contexts/SubscriptionContext";
import useSubscriptionPlan from "../../hooks/useSubscriptionPlan";
import { FALLBACK_PLAN_FEATURES } from "../../subscription/planFeatures";
import { useModuleEnabled } from "../../modules/ModulesProvider";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

interface RequireSubscriptionProps {
  children: React.ReactNode;
}

export default function RequireSubscription({ children }: RequireSubscriptionProps) {
  const { hasActiveSubscription, isLoading } = useSubscription();
  // The server's configured list wins; FALLBACK_PLAN_FEATURES is what shows when it is absent.
  const { planDetails } = useSubscriptionPlan();
  const billingEnabled = useModuleEnabled("billing");
  const navigate = useNavigate();

  if (isLoading) {
    return null; // Or a loading spinner
  }

  // CL mode (billing module off): no subscriptions/paywall — patients get full access.
  if (!billingEnabled) {
    return <>{children}</>;
  }

  if (!hasActiveSubscription) {
    return (
      <Dialog
        open={true}
        maxWidth="sm"
        fullWidth
        onClose={() => navigate("/dashboard")}
        slotProps={{
          paper: { sx: { borderRadius: 3 } }
        }}
      >
        <DialogTitle>
          <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
            Subscription Required
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3}>
            <Typography>
              To schedule appointments and access our full range of services, you'll need an active subscription.
            </Typography>
            {/* The background was the hardcoded light blue #eaf6fb while the text used
                text.primary — which is near-white in dark mode, so this panel rendered white text
                on a near-white card and was unreadable. Tinting from the theme's own info colour
                keeps it legible in both modes; a fixed light value cannot. Now brand.panel.info. */}
            <Box
              sx={{
                bgcolor: (theme) => theme.palette.brand.panel.info,
                border: (theme) => `1.5px solid ${alpha(theme.palette.info.main, 0.4)}`,
                borderRadius: 2,
                p: 2.5,
                mb: 1,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  // info.dark is a dark blue, invisible on a dark panel. info.main reads in both.
                  color: "info.main",
                  fontWeight: 600,
                  mb: 1
                }}>
                What you get:
              </Typography>
              <Stack spacing={1.2}>
                {(planDetails?.features ?? FALLBACK_PLAN_FEATURES).map((feature, idx) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon color="info" fontSize="small" />
                    <Typography variant="body2" sx={{
                      color: "text.primary"
                    }}>{feature}</Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            variant="outlined"
            onClick={() => navigate("/dashboard")}
            sx={{ borderRadius: 2, minWidth: 120 }}
          >
            Maybe Later
          </Button>
          <Button
            variant="contained"
            onClick={() => navigate("/subscription")}
            sx={{ borderRadius: 2, minWidth: 120 }}
          >
            View Plans
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return <>{children}</>;
} 